import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Menu, 
  X, 
  Home, 
  LogOut,
  Bell,
  MapPin,
  ChevronDown,
  Map,
  Sprout,
  Users,
  Eye,
  Wrench,
  Leaf,
  Calendar,
  Plus,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Settings,
  FileText,
  User
} from 'lucide-react';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [managementExpanded, setManagementExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Role-based navigation menu
  const getNavigation = () => {
    if (user?.role === 'gotn-official') {
      return [
        { name: 'Dashboard', href: '/dashboard', icon: Home },
        { name: 'Trial Creation', href: '/trial-creation', icon: Plus },
      ];
    }
    if (user?.role === 'plot-officer' || user?.role === 'officer') {
      return [
        { name: 'Officer Workspace', href: '/officer', icon: Home },
      ];
    }
    
    if (user?.role === 'scientist') {
      return [
        { name: 'Dashboard', href: '/scientist', icon: Home },
        { 
          name: 'Management', 
          icon: Settings,
          subItems: [
            { name: 'Observation', href: '/observation', icon: Eye },
            { name: 'Cultural Operations', href: '/field-operations', icon: Wrench },
            { name: 'Soil Type', href: '/soil-management', icon: Leaf },
            { name: 'Season', href: '/season', icon: Calendar },
          ]
        },
        { name: 'Trial Creation', href: '/trial-creation', icon: Plus },
      ];
    }
    
    // Admin and Incharge navigation
      return [
        { name: 'Dashboard', href: '/dashboard', icon: Home },
        { 
          name: 'Management', 
          icon: Settings,
          subItems: [
            { name: 'Location', href: '/location', icon: Map },
            { name: 'Crops', href: '/crop-management', icon: Sprout },
            { name: 'Officer Incharge', href: '/officer-incharge', icon: Users },
            { name: 'Observation', href: '/observation', icon: Eye },
            { name: 'Cultural Operations', href: '/field-operations', icon: Wrench },
            { name: 'Soil Type', href: '/soil-management', icon: Leaf },
            { name: 'Season', href: '/season', icon: Calendar },
          ]
        },
        { name: 'Trial Creation', href: '/trial-creation', icon: Plus },
        { name: 'Trial Data Entry', href: '/trial-data-entry', icon: FileText },
        { name: 'View Data Entry', href: '/view-data-entry', icon: Eye },
      ];
  };

  const navigation = getNavigation();

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false); // Close mobile sidebar on desktop
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-expand Management menu when on sub-pages
  useEffect(() => {
    const currentPath = location.pathname;
    const managementPaths = ['/location', '/crop-management', '/officer-incharge', '/observation', '/field-operations', '/soil-management', '/season'];
    
    if (managementPaths.includes(currentPath)) {
      setManagementExpanded(true);
    }
  }, [location.pathname]);

  const handleLogout = (e) => {
    alert('Logout button clicked!'); // Test if click is working
    console.log('handleLogout called with event:', e);
    console.log('Event type:', e.type);
    console.log('Event target:', e.target);
    console.log('Current user:', user);
    
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('About to call logout function');
    setProfileDropdownOpen(false);
    logout();
    console.log('Logout function called successfully');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{ width: sidebarCollapsed ? '64px' : '256px' }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 bg-primary-600 rounded-lg mr-3">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              {!sidebarCollapsed && (
                <span className="text-xl font-bold text-gray-900">TNAU</span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {/* Hamburger toggle button */}
              <button
                onClick={toggleSidebarCollapse}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {sidebarCollapsed ? (
                  <ChevronRight className="w-4 h-4" />
                ) : (
                  <ChevronLeft className="w-4 h-4" />
                )}
              </button>
              {/* Mobile close button */}
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 py-6 space-y-2 overflow-y-auto ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
            {navigation.map((item) => {
              const Icon = item.icon;
              
              // If item has subItems, render as expandable menu
              if (item.subItems) {
                return (
                  <div key={item.name} className="space-y-1">
                    <button
                      onClick={() => setManagementExpanded(!managementExpanded)}
                      className={`w-full flex items-center justify-between py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        isActive(item.href) || item.subItems.some(subItem => isActive(subItem.href))
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      } ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'}`}
                      title={sidebarCollapsed ? item.name : ''}
                    >
                      <div className="flex items-center">
                        <Icon 
                          className={`w-5 h-5 text-current ${sidebarCollapsed ? '' : 'mr-3'}`}
                          style={{ display: 'block', visibility: 'visible', opacity: 1 }}
                        />
                        {!sidebarCollapsed && item.name}
                      </div>
                      {!sidebarCollapsed && (
                        <ChevronDown 
                          className={`w-4 h-4 transition-transform duration-200 ${
                            managementExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </button>
                    
                    {/* Submenu items */}
                    {managementExpanded && !sidebarCollapsed && (
                      <div className="ml-4 space-y-1">
                        {item.subItems.map((subItem) => {
                          const SubIcon = subItem.icon;
                          return (
                            <button
                              key={subItem.name}
                              onClick={() => {
                                navigate(subItem.href);
                                setSidebarOpen(false);
                              }}
                              className={`w-full flex items-center py-2 px-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                                isActive(subItem.href)
                                  ? 'bg-primary-50 text-primary-600 border-l-2 border-primary-500'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              <SubIcon className="w-4 h-4 mr-3" />
                              {subItem.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular navigation item
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center py-3 text-sm font-medium rounded-lg transition-colors duration-200 nav-item ${
                    isActive(item.href)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  } ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'}`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <Icon 
                    className={`w-5 h-5 text-current ${sidebarCollapsed ? '' : 'mr-3'}`}
                    style={{ display: 'block', visibility: 'visible', opacity: 1 }}
                  />
                  {!sidebarCollapsed && item.name}
                </button>
              );
            })}
          </nav>

          {/* User info */}
          <div className={`border-t border-gray-200 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''}`}>
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-700">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              {!sidebarCollapsed && (
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role === 'officer' ? 'Officer Incharge' : user?.role}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Header */}
        <header 
          className={`bg-white shadow-sm border-b border-gray-200 fixed top-0 right-0 left-0 z-30 ${sidebarCollapsed ? 'lg:left-16' : 'lg:left-64'}`}
        >
          <div className="flex items-center justify-between h-14 sm:h-12 px-4 sm:px-6 lg:px-8">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Header content */}
            <div className="flex-1 flex items-center justify-between">
              <div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
                  <span className="hidden sm:inline">
                    {user?.role === 'plot-officer' ? 'TNAU Plot Officer Dashboard' : 
                     user?.role === 'scientist' ? 'TNAU Scientist Dashboard' : 
                     'TNAU Research Trial Management'}
                  </span>
                  <span className="sm:hidden">TNAU</span>
                </h1>
              </div>

              {/* Right side */}
              <div className="flex items-center space-x-4">
                {/* Notifications */}
                <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                  <Bell className="w-6 h-6" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </button>

                {/* Profile dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    className="flex items-center space-x-2 p-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-700">
                        {user?.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {profileDropdownOpen && (
                    <div 
                      className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[60]"
                      data-dropdown
                    >
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      <a
                        href="/profile"
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors cursor-pointer border-0 outline-none focus:outline-none focus:ring-2 focus:ring-primary-500 no-underline"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Profile
                      </a>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleLogout(e);
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors cursor-pointer border-0 outline-none focus:outline-none focus:ring-2 focus:ring-primary-500 no-underline"
                        data-testid="logout-button"
                        style={{
                          position: 'relative',
                          zIndex: 70,
                          pointerEvents: 'auto',
                          userSelect: 'none'
                        }}
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="px-4 sm:px-6 lg:px-8 pt-16 sm:pt-14 lg:pt-14 pb-4 sm:pb-6 lg:pb-8">
          {children}
        </main>
      </div>

      {/* Close dropdown when clicking outside - temporarily removed for testing */}
      {/* {profileDropdownOpen && (
        <div 
          className="fixed inset-0 z-40"
          style={{
            pointerEvents: 'auto'
          }}
          onClick={(e) => {
            // Don't close if clicking on the dropdown itself
            if (e.target.closest('[data-dropdown]')) {
              return;
            }
            setProfileDropdownOpen(false);
          }}
        />
      )} */}
    </div>
  );
};

export default Layout; 